import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "container" }
const _hoisted_2 = { class: "d-flex flex-wrap justify-content-between align-items-center py-3 mb-4 border-bottom sticky-top" }
const _hoisted_3 = {
  key: 0,
  class: "fs-4"
}
const _hoisted_4 = {
  key: 0,
  class: "nav nav-pills mt-1 mt-md-0"
}
const _hoisted_5 = { class: "nav-item" }
const _hoisted_6 = {
  key: 0,
  class: "nav-item dropdown",
  id: "dropdownLanguageSwitcher"
}
const _hoisted_7 = {
  class: "dropdown-menu overflow-auto",
  style: {"max-height":"300px"},
  "aria-labelledby": "languageDropdown"
}
const _hoisted_8 = ["onClick"]
const _hoisted_9 = ["src", "alt"]
const _hoisted_10 = {
  key: 1,
  class: "me-2",
  style: {"width":"20px","display":"inline-block"}
}
const _hoisted_11 = {
  key: 1,
  class: "nav-item dropdown",
  id: "dropdownThemeSwitcher"
}
const _hoisted_12 = {
  class: "btn btn-outline-info dropdown-toggle border-0",
  role: "button",
  "data-bs-toggle": "dropdown",
  "aria-expanded": "false",
  style: {"cursor":"pointer"}
}
const _hoisted_13 = {
  class: "dropdown-menu",
  "aria-labelledby": "themeSwitcher"
}
const _hoisted_14 = { class: "nav-item" }
const _hoisted_15 = ["data-user-id", "data-user-name"]
const _hoisted_16 = { class: "dropdown-menu" }
const _hoisted_17 = { class: "dropdown-item-text fw-bold text-center" }
const _hoisted_18 = {
  key: 1,
  class: "nav nav-pills mt-1 mt-md-0"
}

import { ref, computed, onMounted, onBeforeUnmount, watch, getCurrentInstance } from 'vue';
import { useStore } from 'vuex';
import { useRouteAccess } from '@/composables/useRouteAccess';
import { useLanguageModal } from '@/composables/useLanguageModal';
import { useTranslation } from '@/composables/useTranslation';
import { RecommendedLanguage } from '@/store/modules/recommendedLanguages';
import SkeletonText from '@/components/ui/SkeletonText.vue';


export default /*@__PURE__*/_defineComponent({
  __name: 'TopMenu',
  emits: ['actionLogout'],
  setup(__props, { emit: __emit }) {

const emit = __emit;
const store = useStore();
const { openModal } = useLanguageModal();
const { tg } = useTranslation();
const internalInstance = getCurrentInstance();
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const wsSend = internalInstance?.appContext.config.globalProperties.$wsSend as (signal: string, payload?: any) => void;

const themes = {
  none: 'bi bi-slash-circle',
  light: 'bi bi-brightness-high',
  dark: 'bi bi-moon',
  auto: 'bi bi-circle-half',
};

const themeInterval = ref<number | null>(null);
const routeAccess = ref(useRouteAccess());

const user = computed(() => store.state.auth.user);
const wsConnected = computed(() => store.state.connection.wsConnected);
const theme = computed(() => store.state.theme.theme);
const themeDoNotShow = computed(() => store.state.theme.themeDoNotShow);
const languageSwitcherDoNotShow = computed(() => store.state.i18n.languageSwitcherDoNotShow);

const recommendedLanguages = computed(() => {
  const languages = store.getters['recommendedLanguages/getUniqueLanguages'];
  return languages.sort((a: RecommendedLanguage, b: RecommendedLanguage) => a.weight < b.weight ? 1 : (a.weight > b.weight ? -1 : 0));
});

const idLanguage = computed(() => store.state.i18n.idLanguage);

const isSessionInit = computed(() => routeAccess.value?.isSessionInit || false);
const currentIcon = computed(() => themes[theme.value ?? 'none']);

// Определяем, загружены ли переводы для session группы
const isTranslationsLoaded = computed(() => {
  return store.state.i18n.translateGroups.session && 
         Object.keys(store.state.i18n.translateGroups.session).length > 0;
});

function applyTheme() {
  if (theme.value === null) {
    document.documentElement.removeAttribute('data-bs-theme');
  } else if (theme.value === 'auto') {
    const autoMode = window.matchMedia('(prefers-color-scheme: light)').matches;
    document.documentElement.setAttribute('data-bs-theme', autoMode ? 'light' : 'dark');
  } else {
    document.documentElement.setAttribute('data-bs-theme', theme.value);
  }
}

function updateThemeIfAuto() {
  if (theme.value === 'auto') {
    applyTheme();
  }
}

function setTopMenuTheme(newTheme: string) {
  store.dispatch('theme/setTheme', newTheme);
  if (user.value.theme !== theme.value && user.value.idUser !== null) {
    wsSend('profileUpdateTheme', {
      newTheme,
    });
  }
}

function selectLanguageModal() {
  openModal();
}

function selectLanguage(idLanguage: number | null, idLocale: number | null) {
  // Если idLanguage null - это автовыбор, передаем null значения
  if (idLanguage === null) {
    wsSend('profileUpdateLocale', {
      idLanguage: null,
      idLocale: null,
    });
    return;
  }

  // В новой структуре данных idLocale уже приходит с языком
  // Если idLocale все еще null, найдем его в recommendedLanguages
  if (idLocale === null) {
    const recommendedLanguage = recommendedLanguages.value.find((lang: RecommendedLanguage) => lang.idLanguage === idLanguage);
    if (recommendedLanguage) {
      idLocale = recommendedLanguage.idLocale;
    }
  }

  wsSend('profileUpdateLocale', {
    idLanguage,
    idLocale,
  });
}

function hideTopMenuLanguageSwitcher() {
  store.dispatch('i18n/setLanguageSwitcherDoNotShow', true);
}

function hideTopMenuTheme() {
  store.dispatch('theme/setThemeDoNotShow', true);
}

function actionLogout() {
  emit('actionLogout');
}

onMounted(() => {
  applyTheme();
  themeInterval.value = window.setInterval(updateThemeIfAuto, 60000);
});

onBeforeUnmount(() => {
  if (themeInterval.value) {
    clearInterval(themeInterval.value);
  }
});

watch(theme, () => {
  applyTheme();
});

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("header", _hoisted_2, [
      _createVNode(_component_router_link, {
        to: "/",
        class: "navbar-brand d-flex align-items-center"
      }, {
        default: _withCtx(() => [
          _cache[4] || (_cache[4] = _createElementVNode("img", {
            src: "/logo_28x28.png",
            alt: "Logo",
            class: "me-2",
            width: "28",
            height: "28"
          }, null, -1)),
          (isTranslationsLoaded.value)
            ? (_openBlock(), _createElementBlock("span", _hoisted_3, _toDisplayString(_unref(tg)('session', 'title')), 1))
            : (_openBlock(), _createBlock(SkeletonText, {
                key: 1,
                width: "120px",
                height: "1.5rem"
              }))
        ]),
        _: 1
      }),
      (wsConnected.value && isSessionInit.value)
        ? (_openBlock(), _createElementBlock("ul", _hoisted_4, [
            _createElementVNode("li", _hoisted_5, [
              (user.value?.admin)
                ? (_openBlock(), _createBlock(_component_router_link, {
                    key: 0,
                    to: "/admin",
                    class: "btn btn-outline-info border-0"
                  }, {
                    default: _withCtx(() => _cache[5] || (_cache[5] = [
                      _createElementVNode("i", { class: "bi bi-gear-fill" }, null, -1)
                    ])),
                    _: 1
                  }))
                : _createCommentVNode("", true)
            ]),
            (languageSwitcherDoNotShow.value === false)
              ? (_openBlock(), _createElementBlock("li", _hoisted_6, [
                  _cache[11] || (_cache[11] = _createElementVNode("button", {
                    class: "btn btn-outline-info dropdown-toggle border-0",
                    type: "button",
                    id: "languageDropdown",
                    "data-bs-toggle": "dropdown",
                    "aria-expanded": "false"
                  }, [
                    _createElementVNode("i", { class: "bi bi-translate" })
                  ], -1)),
                  _createElementVNode("ul", _hoisted_7, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(recommendedLanguages.value, (language) => {
                      return (_openBlock(), _createElementBlock("li", {
                        key: language.idLanguage || language.languageCode
                      }, [
                        _createElementVNode("button", {
                          class: _normalizeClass(["dropdown-item w-100 text-start d-flex align-items-center", {
                    'active bg-info text-white': language.idLanguage === idLanguage.value,
                  }]),
                          onClick: ($event: any) => (selectLanguage(language.idLanguage, language.idLocale)),
                          type: "button"
                        }, [
                          (language.countryCode)
                            ? (_openBlock(), _createElementBlock("img", {
                                key: 0,
                                src: '/images/country/' + language.countryCode + '/20x16.png',
                                alt: language.countryCode + ' flag',
                                class: "flag-icon me-2",
                                style: {"width":"20px","height":"16px"}
                              }, null, 8, _hoisted_9))
                            : (_openBlock(), _createElementBlock("span", _hoisted_10)),
                          _createTextVNode(" " + _toDisplayString(language.selfTitle), 1)
                        ], 10, _hoisted_8)
                      ]))
                    }), 128)),
                    _cache[8] || (_cache[8] = _createElementVNode("li", null, [
                      _createElementVNode("hr", { class: "dropdown-divider" })
                    ], -1)),
                    _createElementVNode("li", null, [
                      _createElementVNode("button", {
                        class: _normalizeClass(["dropdown-item w-100 text-start", { 'active bg-info text-white': idLanguage.value === null }]),
                        onClick: _cache[0] || (_cache[0] = ($event: any) => (selectLanguage(null, null))),
                        type: "button"
                      }, _cache[6] || (_cache[6] = [
                        _createElementVNode("i", { class: "bi bi-arrow-clockwise me-2" }, null, -1),
                        _createTextVNode(" Автовыбор языка ")
                      ]), 2)
                    ]),
                    _cache[9] || (_cache[9] = _createElementVNode("li", null, [
                      _createElementVNode("hr", { class: "dropdown-divider" })
                    ], -1)),
                    _createElementVNode("li", null, [
                      _createElementVNode("button", {
                        class: "dropdown-item w-100 text-center",
                        onClick: selectLanguageModal,
                        type: "button"
                      }, " Все языки ")
                    ]),
                    _cache[10] || (_cache[10] = _createElementVNode("li", null, [
                      _createElementVNode("hr", { class: "dropdown-divider" })
                    ], -1)),
                    _createElementVNode("li", null, [
                      _createElementVNode("button", {
                        class: "dropdown-item w-100 text-start",
                        onClick: hideTopMenuLanguageSwitcher,
                        type: "button"
                      }, _cache[7] || (_cache[7] = [
                        _createElementVNode("i", { class: "bi bi-x-lg" }, null, -1),
                        _createTextVNode(" Убрать из меню ")
                      ]))
                    ])
                  ])
                ]))
              : _createCommentVNode("", true),
            (themeDoNotShow.value === false)
              ? (_openBlock(), _createElementBlock("li", _hoisted_11, [
                  _createElementVNode("span", _hoisted_12, [
                    _createElementVNode("i", {
                      class: _normalizeClass(currentIcon.value)
                    }, null, 2)
                  ]),
                  _createElementVNode("ul", _hoisted_13, [
                    _createElementVNode("li", null, [
                      _createElementVNode("span", {
                        class: "dropdown-item",
                        onClick: _cache[1] || (_cache[1] = () => setTopMenuTheme('light')),
                        style: {"cursor":"pointer"}
                      }, [
                        _cache[12] || (_cache[12] = _createElementVNode("i", { class: "bi bi-brightness-high" }, null, -1)),
                        _createTextVNode(" " + _toDisplayString(_unref(tg)('session', 'theme_light')), 1)
                      ])
                    ]),
                    _createElementVNode("li", null, [
                      _createElementVNode("span", {
                        class: "dropdown-item",
                        onClick: _cache[2] || (_cache[2] = () => setTopMenuTheme('dark')),
                        style: {"cursor":"pointer"}
                      }, [
                        _cache[13] || (_cache[13] = _createElementVNode("i", { class: "bi bi-moon" }, null, -1)),
                        _createTextVNode(" " + _toDisplayString(_unref(tg)('session', 'theme_dark')), 1)
                      ])
                    ]),
                    _createElementVNode("li", null, [
                      _createElementVNode("span", {
                        class: "dropdown-item",
                        onClick: _cache[3] || (_cache[3] = () => setTopMenuTheme('auto')),
                        style: {"cursor":"pointer"}
                      }, [
                        _cache[14] || (_cache[14] = _createElementVNode("i", { class: "bi bi-circle-half" }, null, -1)),
                        _createTextVNode(" " + _toDisplayString(_unref(tg)('session', 'theme_auto')), 1)
                      ])
                    ]),
                    _cache[16] || (_cache[16] = _createElementVNode("li", null, [
                      _createElementVNode("hr", { class: "dropdown-divider" })
                    ], -1)),
                    _createElementVNode("li", null, [
                      _createElementVNode("span", {
                        class: "dropdown-item",
                        onClick: hideTopMenuTheme,
                        style: {"cursor":"pointer"}
                      }, [
                        _cache[15] || (_cache[15] = _createElementVNode("i", { class: "bi bi-x-lg" }, null, -1)),
                        _createTextVNode(" " + _toDisplayString(_unref(tg)('session', 'theme_removeFromMenu')), 1)
                      ])
                    ])
                  ])
                ]))
              : _createCommentVNode("", true),
            _createElementVNode("li", _hoisted_14, [
              (!user.value?.name)
                ? (_openBlock(), _createBlock(_component_router_link, {
                    key: 0,
                    to: "/profile",
                    class: "btn btn-outline-info dropdown-toggle border-0"
                  }, {
                    default: _withCtx(() => _cache[17] || (_cache[17] = [
                      _createElementVNode("i", { class: "bi bi-person" }, null, -1)
                    ])),
                    _: 1
                  }))
                : _createCommentVNode("", true)
            ]),
            (user.value?.name)
              ? (_openBlock(), _createElementBlock("li", {
                  key: 2,
                  class: "nav-item dropdown",
                  id: "dropdownProfile",
                  "data-user-id": user.value.idUser,
                  "data-user-name": user.value.name
                }, [
                  _cache[21] || (_cache[21] = _createElementVNode("button", {
                    class: "btn btn-outline-info dropdown-toggle border-0",
                    role: "button",
                    "data-bs-toggle": "dropdown",
                    "aria-expanded": "false"
                  }, [
                    _createElementVNode("i", { class: "bi bi-person-circle" })
                  ], -1)),
                  _createElementVNode("ul", _hoisted_16, [
                    _createElementVNode("li", null, [
                      _createElementVNode("span", _hoisted_17, _toDisplayString(user.value.name), 1)
                    ]),
                    _cache[18] || (_cache[18] = _createElementVNode("li", null, [
                      _createElementVNode("hr", { class: "dropdown-divider" })
                    ], -1)),
                    _createElementVNode("li", null, [
                      _createVNode(_component_router_link, {
                        to: "/helper",
                        class: "dropdown-item"
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(_unref(tg)('session', 'helper')), 1)
                        ]),
                        _: 1
                      })
                    ]),
                    _cache[19] || (_cache[19] = _createElementVNode("li", null, [
                      _createElementVNode("hr", { class: "dropdown-divider" })
                    ], -1)),
                    _createElementVNode("li", null, [
                      _createVNode(_component_router_link, {
                        to: "/profile",
                        class: "dropdown-item"
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(_unref(tg)('session', 'profile')), 1)
                        ]),
                        _: 1
                      })
                    ]),
                    _cache[20] || (_cache[20] = _createElementVNode("li", null, [
                      _createElementVNode("hr", { class: "dropdown-divider" })
                    ], -1)),
                    _createElementVNode("li", null, [
                      _createElementVNode("a", {
                        class: "dropdown-item",
                        href: "javascript:void(0)",
                        onClick: actionLogout
                      }, _toDisplayString(_unref(tg)('session', 'logout')), 1)
                    ])
                  ])
                ], 8, _hoisted_15))
              : _createCommentVNode("", true)
          ]))
        : (_openBlock(), _createElementBlock("ul", _hoisted_18, _cache[22] || (_cache[22] = [
            _createElementVNode("li", { class: "nav-item" }, [
              _createElementVNode("div", {
                class: "spinner-border",
                role: "status"
              }, [
                _createElementVNode("span", { class: "visually-hidden" }, "Loading...")
              ])
            ], -1)
          ])))
    ])
  ]))
}
}

})